import React, { useEffect, useState } from 'react';
import { globalActionsService } from '../services/globalActionsService';
import { GlobalAction } from '../types/GlobalAction';
import GlobalActionList from './GlobalActionList';
import GlobalActionForm from './GlobalActionForm';
import GlobalActionSearchFilter from './GlobalActionSearchFilter';
import RightOverlay from 'common/components/overlay/RightOverlay';

const SDG_DEFAULT = 13;

const GlobalActionManager: React.FC = () => {
  const [actions, setActions] = useState<GlobalAction[]>([]);
  const [filteredActions, setFilteredActions] = useState<GlobalAction[]>([]);
  const [selectedAction, setSelectedAction] = useState<GlobalAction | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSdg, setSelectedSdg] = useState<'all' | number>(SDG_DEFAULT);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchActions = async (sdg: number) => {
    setIsLoading(true);
    const result = (await globalActionsService.list(sdg)).records;
    setActions(result);
    applyFilters(result, searchQuery); // Filter with current search term
    setIsLoading(false);
  };

  useEffect(() => {
    fetchActions(selectedSdg === 'all' ? -1 : selectedSdg);
  }, [selectedSdg]);

  const applyFilters = (data: GlobalAction[], query: string) => {
    const q = query.toLowerCase();
    setSearchQuery(query);
    const filtered = data.filter((a) =>
      a.title?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q)
    );
    setFilteredActions(filtered);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    applyFilters(actions, query);
  };

  const handleFilterChange = (sdg: 'all' | number) => {
    setSelectedSdg(sdg);
  };

  const handleSave = async (data: Partial<GlobalAction>) => {
    setIsSaving(true);
    if (data.id) {
      await globalActionsService.update(data.id, data);
    } else {
      await globalActionsService.create(data);
    }
    await fetchActions(selectedSdg === 'all' ? -1 : selectedSdg);
    setIsSaving(false);
    setIsEditOpen(false);
    setSelectedAction(undefined);
  };

  const handleEditClick = (action: GlobalAction) => {
    setSelectedAction(action);
    setIsEditOpen(true);
  };

  const handleItemDelete = async (index: number) => {
    const thisAction = actions && actions.length >= 0 ? actions[index] : null;
    if (thisAction && thisAction.id) {
      try{
        await globalActionsService.remove(thisAction.id);
      }
      catch(e:any){}
    }

    const updatedItems = [...actions];
    updatedItems.splice(index, 1);
    setActions(updatedItems);
    applyFilters(updatedItems, searchQuery);
    
  } 

  return (
    <div className="container">
      <div className="row">
        <GlobalActionSearchFilter
          searchQuery={searchQuery}
          onSearch={handleSearch}
          selectedSdg={selectedSdg}
          onFilterChange={handleFilterChange}
          itemCount={filteredActions.length}
          onCreate={() => {
            setSelectedAction(undefined);
            setIsEditOpen(true);
          }}
        />
      </div>

      <GlobalActionList
        sdg={selectedSdg}
        list={filteredActions}
        onEditClick={handleEditClick}
        onDeleteItem={handleItemDelete}
        isLoading={isLoading}
      />

      <RightOverlay
        onClose={() => setIsEditOpen(false)}
        isOpen={isEditOpen}
      >
        <div className='mt-4'>
          <GlobalActionForm
            key={selectedAction?.id ?? `new-${selectedSdg ?? 'all'}-${Date.now()}`}
            sdg={selectedSdg}
            onSave={handleSave}
            isLoading={isSaving}
            initialData={selectedAction}
          />
        </div>
      </RightOverlay>
    </div>
  );
};

export default GlobalActionManager;
